// src/lib/recurringJobs.ts
//
// LOCKSTEP: occurrence math duplicated in supabase/functions/process-scheduled-tasks/_recurrence.ts
// (computeOccurrencesInline / buildOccurrenceJobsInline) — edit both.
//
// Session 35 / Chunk 2. Pure, deterministic occurrence generator for the
// materialized recurring-jobs feature. The series row (from Chunk 1's
// recurring_job_series table) holds the rule + a JSONB template of the
// per-occurrence defaults; this module turns that rule into an array of
// concrete dates over a window, plus a thin builder that turns those dates
// into field_jobs Insert payloads.
//
// Design notes:
//   - No DB calls, no Supabase, no side effects. Easy to reason about,
//     trivial to test, no async surface. The Chunk 3 UI imports this
//     directly; the future scheduled-function keeper will too.
//   - date-fns for all calendar math. Raw Date arithmetic on DST/month-end
//     boundaries is a footgun (Feb-28-to-Mar-1 on a leap year, e.g.).
//   - `count` tracks chronological occurrence index from startDate forward
//     — NOT within-window. endAfterCount caps total occurrences across
//     time; the window only restricts what's RETURNED.
//   - Monthly clamp: the "31st" series clamps to last-day-of-month for
//     shorter months (Jan 31 → Feb 28 → Mar 31 → Apr 30). Walking via
//     addMonths(start, n) preserves the 31st intent through the clamp,
//     unlike walking from the prior occurrence which would degrade
//     permanently (Jan 31 → Feb 28 → Mar 28 — wrong).
//   - Biweekly anchor: the week containing startDate is week 0.
//     Occurrences fire on even-indexed weeks (0, 2, 4, ...) on each day
//     in daysOfWeek that's >= startDate.
//   - daysOfWeek is unused for monthly (the day-of-month from start_date
//     is the recurrence axis). The schema requires NOT NULL so the
//     create UI passes [] for monthly.
// ============================================================================

import {
  addDays, addMonths, endOfMonth, format, getDate, parseISO, setDate, startOfWeek,
} from 'date-fns'
import type { Database } from '@/lib/database.types'

export type RecurFrequency = 'weekly' | 'biweekly' | 'monthly'

export interface RecurrenceRule {
  frequency: RecurFrequency
  /** 0=Sun..6=Sat. Single-element for weekly/biweekly single-day, multi for M/W/F. Unused for monthly. */
  daysOfWeek: number[]
  /** ISO YYYY-MM-DD. */
  startDate: string
  /** ISO YYYY-MM-DD, inclusive. Null/undefined = open-ended. */
  endDate?: string | null
  /** Alternative to endDate. Total occurrences from startDate. Null = no count cap. */
  endAfterCount?: number | null
}

/**
 * Compute occurrence dates for a recurrence rule within [windowStart, windowEnd].
 *
 * Returns YYYY-MM-DD strings, ascending-sorted, deduped. See module comment
 * for the count/window/clamp semantics.
 */
export function computeOccurrences(
  rule: RecurrenceRule,
  windowStart: string,
  windowEnd: string,
): string[] {
  const out: string[] = []
  const start   = parseISO(rule.startDate)
  const winStart = parseISO(windowStart)
  const winEnd   = parseISO(windowEnd)
  const hardEnd  = rule.endDate ? parseISO(rule.endDate) : null
  const maxCount = rule.endAfterCount ?? Infinity

  // Empty daysOfWeek for weekly/biweekly is a no-op — operator picked a
  // recurring job but no days. Return empty rather than throw.
  if ((rule.frequency === 'weekly' || rule.frequency === 'biweekly') && rule.daysOfWeek.length === 0) {
    return out
  }

  let count = 0

  // Returns 'stop' when the iteration should terminate (hard end reached
  // or count cap hit). Counter is bumped per chronological occurrence,
  // regardless of whether the date lands inside the window.
  const tryAdd = (date: Date): 'continue' | 'stop' => {
    if (date < start) return 'continue'
    if (hardEnd && date > hardEnd) return 'stop'
    if (count >= maxCount) return 'stop'
    if (date >= winStart && date <= winEnd) {
      out.push(format(date, 'yyyy-MM-dd'))
    }
    count++
    return 'continue'
  }

  if (rule.frequency === 'monthly') {
    const dom = getDate(start)
    let monthOffset = 0
    while (count < maxCount) {
      const targetMonth = addMonths(start, monthOffset)
      if (targetMonth > winEnd && monthOffset > 0) break
      const monthEndDay = getDate(endOfMonth(targetMonth))
      const date = setDate(targetMonth, Math.min(dom, monthEndDay))
      const result = tryAdd(date)
      if (result === 'stop') break
      monthOffset++
      if (monthOffset > 600) break // 50-year safety cap
    }
  } else {
    const stepWeeks = rule.frequency === 'biweekly' ? 2 : 1
    const dows = [...rule.daysOfWeek].sort((a, b) => a - b)
    let weekCursor = startOfWeek(start)
    let weekIdx = 0
    let stop = false
    while (count < maxCount && weekCursor <= winEnd && !stop) {
      if (weekIdx % stepWeeks === 0) {
        for (const dow of dows) {
          const date = addDays(weekCursor, dow)
          if (tryAdd(date) === 'stop') { stop = true; break }
        }
      }
      weekCursor = addDays(weekCursor, 7)
      weekIdx++
      if (weekIdx > 5200) break // 100-year safety cap
    }
  }

  return Array.from(new Set(out)).sort()
}

// ─── Builder ────────────────────────────────────────────────────────────────

export type SeriesRow = Database['public']['Tables']['recurring_job_series']['Row']
export type FieldJobInsert = Database['public']['Tables']['field_jobs']['Insert']

/**
 * Turns a series row into an array of field_jobs Insert payloads for every
 * occurrence inside the window. The caller batch-inserts with
 * `ON CONFLICT (series_id, scheduled_date) DO NOTHING` to make re-runs
 * idempotent (the partial unique index on field_jobs enforces it).
 *
 * Template handling: series.template is a JSONB snapshot of the
 * per-occurrence default fields (service_line_id, assigned_agent_id,
 * vendor_id, address fields, notes, line items, contact_name snapshot, etc).
 * Spread first, then layered with the series-derived fields (workspace_id,
 * contact_id, title, scheduled_date, series_id, status) which always win.
 */
export function buildOccurrenceJobs(
  series: SeriesRow,
  windowStart: string,
  windowEnd: string,
): FieldJobInsert[] {
  const rule: RecurrenceRule = {
    frequency: series.frequency as RecurFrequency,
    daysOfWeek: series.days_of_week,
    startDate: series.start_date,
    endDate: series.end_date,
    endAfterCount: series.end_after_count,
  }
  const dates = computeOccurrences(rule, windowStart, windowEnd)
  const template = (series.template ?? {}) as Record<string, unknown>

  return dates.map(scheduledDate => ({
    ...template,
    workspace_id: series.workspace_id,
    contact_id: series.contact_id,
    title: series.title,
    scheduled_date: scheduledDate,
    status: 'scheduled',
    series_id: series.id,
  })) as FieldJobInsert[]
}
